// src/api/cache.ts
// Simple in-memory cache with Time-To-Live (TTL)

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    ttl: number; // Add TTL per entry
}

const cache = new Map<string, CacheEntry<any>>();
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Generates a unique key for caching based on path and params.
 */
export const generateCacheKey = (path: string, params?: Record<string, any>): string => {
    const sortedParams = params
        ? Object.entries(params)
              .filter(([_, value]) => value !== undefined && value !== null) // Filter out undefined/null params
              .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
              .map(([key, value]) => `${key}=${String(value)}`) // Ensure value is stringified
              .join('&')
        : '';
    // Ensure consistent handling of paths with/without leading/trailing slashes if needed
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedPath}${sortedParams ? `?${sortedParams}` : ''}`;
};


/**
 * Retrieves data from the cache if it exists and hasn't expired.
 */
export const getFromCache = <T>(key: string): T | null => {
    const entry = cache.get(key);
    if (!entry) {
        console.log(`[Cache MISS] Key not found: ${key}`);
        return null;
    }

    const isExpired = Date.now() - entry.timestamp > entry.ttl;

    if (isExpired) {
        console.log(`[Cache STALE] Expired entry removed: ${key}`);
        cache.delete(key); // Remove stale entry
        return null;
    }

    console.log(`[Cache HIT] Fresh entry found: ${key}`);
    return entry.data as T;
};

/**
 * Stores data in the cache with a timestamp and specific TTL.
 */
export const setInCache = <T>(key: string, data: T, ttl: number = DEFAULT_TTL): void => {
    // Basic check to avoid caching huge responses if needed (optional)
    // try {
    //     if (JSON.stringify(data).length > 5 * 1024 * 1024) { // e.g., 5MB limit
    //         console.warn(`[Cache SET] Data for key ${key} might be too large, skipping cache.`);
    //         return;
    //     }
    // } catch (e) {
    //     console.warn(`[Cache SET] Could not estimate size for key ${key}, attempting to cache anyway.`);
    // }

    console.log(`[Cache SET] Storing data for key: ${key} with TTL: ${ttl / 1000}s`);
    const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        ttl: ttl,
    };
    cache.set(key, entry);

    // Optional: Implement cache eviction strategy if memory becomes a concern
    // e.g., Limit cache size, remove least recently used (LRU)
    // if (cache.size > 100) {
    //    const oldestKey = cache.keys().next().value;
    //    cache.delete(oldestKey);
    //    console.log(`[Cache EVICT] Removed oldest entry: ${oldestKey} due to size limit.`);
    // }
};

/**
 * Clears the entire cache.
 */
export const clearCache = (): void => {
    const size = cache.size;
    cache.clear();
    console.log(`[Cache CLEARED] Removed ${size} entries.`);
};

/**
 * Removes a specific entry from the cache.
 * @param key The cache key to remove.
 */
export const invalidateCacheEntry = (key: string): void => {
    const deleted = cache.delete(key);
    if (deleted) {
        console.log(`[Cache INVALIDATE] Removed entry: ${key}`);
    } else {
         console.log(`[Cache INVALIDATE] Key not found, no action taken: ${key}`);
    }
};

/**
 * Invalidates cache entries matching a specific path prefix.
 * Useful for clearing all entries related to a subreddit, for example.
 * @param pathPrefix The prefix to match (e.g., "/r/reactjs")
 */
export const invalidateCacheByPrefix = (pathPrefix: string): void => {
     const keysToDelete: string[] = [];
     const normalizedPrefix = pathPrefix.startsWith('/') ? pathPrefix : `/${pathPrefix}`;
     console.log(`[Cache INVALIDATE] Clearing entries with prefix: ${normalizedPrefix}`);
     for (const key of cache.keys()) {
         if (key.startsWith(normalizedPrefix)) {
             keysToDelete.push(key);
         }
     }
     keysToDelete.forEach(key => cache.delete(key));
     console.log(`[Cache INVALIDATE] Removed ${keysToDelete.length} entries with prefix ${normalizedPrefix}.`);
};


// Ensure cache is cleared on logout (called from clearAuthData in redditApi.ts)